import React, { useEffect, useRef, ReactNode } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    LinearProgress,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { modalStack } from "./ModalStack";

interface ModalWindowProps {
    onClose: () => void;
    children: ReactNode;
    onEscape?: () => void;
    onEnter?: () => void;
    title?: React.ReactNode | string;
    loading?: boolean;
    maxWidth?: number;
    minWidth?: number;
    width?: number;
}

export function ModalWindow({
                                width,
                                minWidth,
                                maxWidth,
                                loading,
                                onClose,
                                children,
                                title,
                                onEnter,
                                onEscape
                            }: ModalWindowProps) {
    const dialogRef = useRef<HTMLDivElement | null>(null);

    // keyboard + modal stack logic
    useEffect(() => {
        modalStack.push(dialogRef.current as HTMLDivElement);

        const handleKeyDown = (event: { key: string }) => {
            const isTop = modalStack[modalStack.length - 1] === dialogRef.current;

            if (!isTop) return;

            if (event.key === "Escape") {
                onClose();
                onEscape && onEscape();
            }

            if (event.key === "Enter") {
                onEnter && onEnter();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            modalStack.pop();
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose, onEnter, onEscape]);

    return (
        <Dialog
            open
            onClose={onClose}
            PaperProps={{
                ref: dialogRef,
                sx: {
                    width: width,
                    position: "relative",
                    maxWidth: maxWidth || 500,
                    minWidth: minWidth,
                    maxHeight: "calc(100vh - 100px)",
                    padding: "20px 0 0",
                    overflow: "visible"
                }
            }}
        >
            {/* Close button */}
            <IconButton
                onClick={onClose}
                size="small"
                sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    width: 32,
                    height: 32
                }}
            >
                <CloseOutlinedIcon />
            </IconButton>

            {/* You can optionally wrap this in DialogTitle if needed */}
            {title && <DialogTitle sx={{paddingTop: 0}}>{title}</DialogTitle>}

            <DialogContent>{children}</DialogContent>

            <DialogActions
                sx={{
                    p: 0,
                    position: "relative"
                }}
            >
                <LinearProgress
                    sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%"
                    }}
                    variant={loading ? "indeterminate" : "determinate"}
                    value={loading ? undefined : 100}
                />
            </DialogActions>
        </Dialog>
    );
}